import type { PoaCompany, PoaStatusEntry } from "../hooks/api/useGetPoaStatus";

export type SourceState = "AVAILABLE" | "PENDING" | "UNAVAILABLE";

export interface CollectionSummary {
  state: "PENDING" | "PARTIAL" | "COMPLETE";
  publicPension: SourceState;
  occupationalAndPrivate: SourceState;
  companiesAnswered: number;
  companiesTotal: number;
  lastUpdatedAt: string | null;
  /** Keyed by `orgKey`. */
  dateByOrgKey: Record<string, string>;
  /**
   * Companies that answered that this customer holds a pension with them. The
   * calculation is the only source of the money, so one of these with no matching
   * holding is a company we know about but cannot show.
   */
  insuredCompanies: { key: string; name: string; logotype: string | null }[];
}

/**
 * Matching key for an organisation number. Registration numbers arrive with and
 * without the 16 prefix and with or without a dash; the last ten digits are the
 * number itself in every spelling.
 */
export const orgKey = (organisationId?: string | null): string =>
  (organisationId ?? "").replace(/\D/g, "").slice(-10);

const uniqueCompanies = (companies: PoaCompany[]): number =>
  new Set(companies.map((company) => company.organisationIdCanonical)).size;

export function summarizeCollection({
  poaStatus,
  hasPublicPensionData,
  hasAnyInsuranceData,
}: {
  poaStatus?: PoaStatusEntry[] | null;
  hasPublicPensionData: boolean;
  hasAnyInsuranceData: boolean;
}): CollectionSummary {
  const entries = poaStatus ?? [];
  // Historic POAs are returned alongside the current one, and counting across all
  // of them would fold a closed POA's companies into the active collection.
  const active = entries.find((entry) => entry.isValid) ?? entries[0] ?? null;

  const answered = Object.values(active?.responsesByDate ?? {}).flat();
  // A company past the backend's deadline stays in `pending` with status NO_ANSWER.
  // Waiting for it would spin the collection spinner forever, so it is not outstanding.
  const outstanding = (active?.pending ?? []).filter(
    (company) => company.customerStatus !== "NO_ANSWER",
  );

  const dateByOrgKey: Record<string, string> = {};
  const insuredByKey = new Map<
    string,
    { name: string; logotype: string | null }
  >();
  let lastUpdatedAt: string | null = null;

  for (const company of answered) {
    // Only an explicit yes counts. The field is optional, so treating a missing one
    // as insurance would invent a warning row for every company that said nothing.
    const key = orgKey(company.organisationIdCanonical);
    if (company.customerStatus === "CUSTOMER" && company.name && key) {
      insuredByKey.set(key, {
        name: company.name,
        logotype: company.logotype ?? null,
      });
    }
  }

  for (const company of [...answered, ...(active?.pending ?? [])]) {
    if (!company.responseDate) {
      continue;
    }
    const key = orgKey(company.organisationIdCanonical);
    if (key) {
      dateByOrgKey[key] = company.responseDate;
    }
    if (!lastUpdatedAt || company.responseDate > lastUpdatedAt) {
      lastUpdatedAt = company.responseDate;
    }
  }

  // The backend computes this over real answers only, so a silent company never moves it.
  lastUpdatedAt = active?.lastAnswerAt ?? lastUpdatedAt;

  const derivedAnswered = uniqueCompanies(answered);
  const companiesAnswered = active?.companiesAnswered ?? derivedAnswered;
  const companiesTotal =
    active?.companiesTotal ??
    uniqueCompanies([...answered, ...(active?.pending ?? [])]);

  const stillWaiting = active?.companiesPending ?? outstanding.length;

  let occupationalAndPrivate: SourceState;
  if (!active) {
    occupationalAndPrivate = "UNAVAILABLE";
  } else if (stillWaiting > 0) {
    occupationalAndPrivate = "PENDING";
  } else if (companiesAnswered > 0 || derivedAnswered > 0) {
    occupationalAndPrivate = "AVAILABLE";
  } else {
    // Every company went silent: nothing is coming, and pretending otherwise would
    // leave the customer watching a spinner that never resolves.
    occupationalAndPrivate = "UNAVAILABLE";
  }

  const publicPension: SourceState = hasPublicPensionData
    ? "AVAILABLE"
    : "PENDING";

  // Resolved, not available: once every company has either answered or gone silent
  // there is nothing left to wait for, and the progress banner must stop promising more.
  const occupationalResolved = occupationalAndPrivate !== "PENDING";

  let state: CollectionSummary["state"];
  if (publicPension === "AVAILABLE" && occupationalResolved) {
    state = "COMPLETE";
  } else if (!hasPublicPensionData && !hasAnyInsuranceData) {
    state = "PENDING";
  } else {
    state = "PARTIAL";
  }

  return {
    state,
    publicPension,
    occupationalAndPrivate,
    companiesAnswered,
    companiesTotal,
    lastUpdatedAt,
    dateByOrgKey,
    insuredCompanies: [...insuredByKey].map(([key, company]) => ({
      key,
      ...company,
    })),
  };
}
